/**
 * Component catalog with Zod schemas for validation.
 * See: specs/017-json-canvas/contracts/component-catalog.schema.json
 */

import React, { lazy } from 'react';
import { z } from 'zod';
import type { ComponentType } from '../../types/canvas';
import {
  MAX_NESTING_DEPTH,
  MAX_PAYLOAD_SIZE,
  type ValidationResult,
} from '../../types/canvas';
import {
  CardComponent,
  TextComponent,
  TableComponent,
  AlertComponent,
  ImageComponent,
  FormComponent,
  ButtonComponent,
} from './components';

// Lazy load Chart component for performance (T072)
// Recharts is a heavy library, so we load it only when needed
const LazyChartComponent = lazy(() =>
  import('./components/Chart').then((module) => ({ default: module.ChartComponent }))
);

// ============================================================================
// Component Props Schemas (Zod v4)
// ============================================================================

export const CardPropsSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  variant: z.enum(['default', 'outlined', 'elevated']).default('default'),
});

export const TextPropsSchema = z.object({
  content: z.string(),
  variant: z.enum(['body', 'heading', 'caption', 'code']).default('body'),
  format: z.enum(['plain', 'markdown']).default('plain'),
});

export const TableColumnSchema = z.object({
  key: z.string(),
  header: z.string(),
  width: z.string().optional(),
  align: z.enum(['left', 'center', 'right']).default('left'),
});

export const TablePropsSchema = z.object({
  columns: z.array(TableColumnSchema).min(1),
  rows: z.array(z.record(z.string(), z.unknown())),
  sortable: z.boolean().default(false),
  pageSize: z.number().int().min(1).max(100).default(10),
});

export const ChartPropsSchema = z.object({
  type: z.enum(['line', 'bar', 'pie', 'area']),
  data: z.array(z.record(z.string(), z.unknown())),
  xKey: z.string(),
  yKey: z.string(),
  title: z.string().optional(),
  xLabel: z.string().optional(),
  yLabel: z.string().optional(),
  colors: z.array(z.string()).optional(),
});

export const FormFieldOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

export const FormFieldSchema = z.object({
  name: z.string(),
  type: z.enum([
    'text',
    'email',
    'password',
    'number',
    'textarea',
    'select',
    'checkbox',
  ]),
  label: z.string(),
  placeholder: z.string().optional(),
  required: z.boolean().default(false),
  defaultValue: z.unknown().optional(),
  options: z.array(FormFieldOptionSchema).optional(),
});

export const FormPropsSchema = z.object({
  fields: z.array(FormFieldSchema).min(1),
  submitLabel: z.string().default('Submit'),
  onSubmit: z.object({ action: z.string() }).optional(),
});

export const ActionDefinitionSchema = z.object({
  name: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
  confirm: z
    .object({
      title: z.string(),
      message: z.string(),
    })
    .optional(),
});

export const ButtonPropsSchema = z.object({
  label: z.string(),
  action: ActionDefinitionSchema,
  variant: z.enum(['primary', 'secondary', 'danger', 'ghost']).default('primary'),
  disabled: z.boolean().default(false),
  loading: z.boolean().default(false),
});

export const AlertPropsSchema = z.object({
  message: z.string(),
  severity: z.enum(['info', 'success', 'warning', 'error']).default('info'),
  title: z.string().optional(),
  dismissible: z.boolean().default(false),
});

export const ImagePropsSchema = z.object({
  src: z.string(),
  alt: z.string(),
  width: z.string().optional(),
  height: z.string().optional(),
  objectFit: z.enum(['contain', 'cover', 'fill', 'none']).default('contain'),
});

// ============================================================================
// Visibility Condition Schema
// ============================================================================

const baseVisibilityCondition = z.union([
  z.boolean(),
  z.object({
    path: z.string(),
    equals: z.unknown().optional(),
  }),
]);

// Create a lazy schema for recursive visibility conditions
export const VisibilityConditionSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.boolean(),
    z.object({
      path: z.string(),
      equals: z.unknown().optional(),
    }),
    z.object({
      and: z.array(VisibilityConditionSchema),
    }),
    z.object({
      or: z.array(VisibilityConditionSchema),
    }),
    z.object({
      not: VisibilityConditionSchema,
    }),
  ])
);

// ============================================================================
// Catalog Registry
// ============================================================================

export interface CatalogSchemaEntry {
  schema: z.ZodType<unknown>;
  hasChildren: boolean;
  description: string;
  /** The React component to render. Undefined for components not yet implemented. */
  component?: React.ComponentType<unknown>;
  /** Whether the component is lazy-loaded (T072) */
  isLazy?: boolean;
}

/**
 * Component catalog mapping component types to their Zod schemas.
 */
export const canvasCatalog: Record<ComponentType, CatalogSchemaEntry> = {
  Card: {
    schema: CardPropsSchema,
    hasChildren: true,
    description: 'Container with optional title and subtitle',
    component: CardComponent as React.ComponentType<unknown>,
  },
  Text: {
    schema: TextPropsSchema,
    hasChildren: false,
    description: 'Text display with optional markdown support',
    component: TextComponent as React.ComponentType<unknown>,
  },
  Table: {
    schema: TablePropsSchema,
    hasChildren: false,
    description: 'Data table with columns and rows',
    component: TableComponent as React.ComponentType<unknown>,
  },
  Chart: {
    schema: ChartPropsSchema,
    hasChildren: false,
    description: 'Data visualization (line, bar, pie, area)',
    component: LazyChartComponent as React.ComponentType<unknown>,
    isLazy: true, // T072: Mark as lazy for Suspense handling
  },
  Form: {
    schema: FormPropsSchema,
    hasChildren: true,
    description: 'Interactive form with fields',
    component: FormComponent as React.ComponentType<unknown>,
  },
  Button: {
    schema: ButtonPropsSchema,
    hasChildren: false,
    description: 'Clickable action trigger',
    component: ButtonComponent as React.ComponentType<unknown>,
  },
  Alert: {
    schema: AlertPropsSchema,
    hasChildren: false,
    description: 'Status or warning message',
    component: AlertComponent as React.ComponentType<unknown>,
  },
  Image: {
    schema: ImagePropsSchema,
    hasChildren: false,
    description: 'Image display with alt text',
    component: ImageComponent as React.ComponentType<unknown>,
  },
};

// ============================================================================
// Component Tree Schema
// ============================================================================

/**
 * Component tree type for recursive schema.
 */
interface ComponentTreeType {
  type: string;
  props: Record<string, unknown>;
  children?: ComponentTreeType[];
  visible?: unknown;
}

/**
 * Component tree schema for validation.
 * Note: This is a shallow schema - deep validation is done by validateTree.
 */
export const ComponentTreeSchema: z.ZodType<ComponentTreeType> = z.object({
  type: z.string(),
  props: z.record(z.string(), z.unknown()),
  children: z.array(z.lazy(() => ComponentTreeSchema)).optional(),
  visible: VisibilityConditionSchema.optional(),
});

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validates a component tree against the catalog schemas.
 *
 * Checks:
 * - Component type exists in catalog
 * - Props pass component's Zod schema
 * - Children are recursively validated
 * - Maximum nesting depth (10 levels)
 * - Maximum payload size (1MB)
 *
 * @param tree - The component tree to validate
 * @param depth - Current nesting depth (internal use)
 * @returns ValidationResult with valid flag and error messages
 */
export function validateTree(
  tree: unknown,
  depth: number = 0
): ValidationResult {
  // Check max nesting depth
  if (depth > MAX_NESTING_DEPTH) {
    return {
      valid: false,
      errors: [`Maximum nesting depth of ${MAX_NESTING_DEPTH} exceeded`],
    };
  }

  // Check payload size on first call
  if (depth === 0) {
    const payloadSize = JSON.stringify(tree).length;
    if (payloadSize > MAX_PAYLOAD_SIZE) {
      return {
        valid: false,
        errors: [
          `Payload size ${payloadSize} bytes exceeds maximum of ${MAX_PAYLOAD_SIZE} bytes`,
        ],
      };
    }
  }

  // Validate tree structure
  if (!tree || typeof tree !== 'object') {
    return { valid: false, errors: ['Invalid tree structure: expected object'] };
  }

  const node = tree as Record<string, unknown>;

  // Check required fields
  if (typeof node.type !== 'string') {
    return { valid: false, errors: ['Missing or invalid "type" field'] };
  }

  if (!node.props || typeof node.props !== 'object') {
    return { valid: false, errors: ['Missing or invalid "props" field'] };
  }

  const componentType = node.type as string;
  const entry = canvasCatalog[componentType as ComponentType];

  // Check component type exists in catalog
  if (!entry) {
    return {
      valid: false,
      errors: [`Unknown component type: ${componentType}`],
    };
  }

  // Validate props against schema
  const propsResult = entry.schema.safeParse(node.props);
  if (!propsResult.success) {
    const errors = propsResult.error.issues.map(
      (e) => `${componentType}.props.${e.path.join('.')}: ${e.message}`
    );
    return { valid: false, errors };
  }

  // Validate children if present
  if (node.children !== undefined) {
    if (!entry.hasChildren) {
      return {
        valid: false,
        errors: [`Component "${componentType}" does not accept children`],
      };
    }

    if (!Array.isArray(node.children)) {
      return {
        valid: false,
        errors: ['Children must be an array'],
      };
    }

    for (let i = 0; i < node.children.length; i++) {
      const childResult = validateTree(node.children[i], depth + 1);
      if (!childResult.valid) {
        return {
          valid: false,
          errors: childResult.errors.map((e) => `children[${i}]: ${e}`),
        };
      }
    }
  }

  // Validate visibility condition if present
  if (node.visible !== undefined) {
    const visibilityResult = VisibilityConditionSchema.safeParse(node.visible);
    if (!visibilityResult.success) {
      return {
        valid: false,
        errors: [`Invalid visibility condition: ${visibilityResult.error.message}`],
      };
    }
  }

  return { valid: true, errors: [] };
}

/**
 * Check if a component type exists in the catalog.
 */
export function isValidComponentType(type: string): type is ComponentType {
  return type in canvasCatalog;
}

/**
 * Get the schema for a component type.
 */
export function getComponentSchema(
  type: ComponentType
): z.ZodType<unknown> | undefined {
  return canvasCatalog[type]?.schema;
}

/**
 * Check if a component type accepts children.
 */
export function componentAcceptsChildren(type: ComponentType): boolean {
  return canvasCatalog[type]?.hasChildren ?? false;
}

// ============================================================================
// Exports for Tool Developers (T071)
// ============================================================================

export {
  CardPropsSchema as CardSchema,
  TextPropsSchema as TextSchema,
  TablePropsSchema as TableSchema,
  ChartPropsSchema as ChartSchema,
  FormPropsSchema as FormSchema,
  ButtonPropsSchema as ButtonSchema,
  AlertPropsSchema as AlertSchema,
  ImagePropsSchema as ImageSchema,
};
